/**
 * Declarative description of a "Funciones" project: the sheets and tables to
 * build, where the answers go, and what each task is graded on. One spec
 * drives both the generic sheet builder and the generic grader, so adding a
 * project means writing data and task text, not Office.js code.
 */

export type CellValue = string | number | boolean;

export interface TableSpec {
  /** Excel table name, unique in the workbook. */
  name: string;
  /** Top-left header cell, e.g. "A4". */
  anchor: string;
  /** Data columns. A value starting with "=" is written as a formula. */
  headers: string[];
  rows: CellValue[][];
  /** Empty, highlighted columns appended after `headers` for the student. */
  answerColumns?: string[];
  /** numberFormat per header (data or answer column). */
  formats?: Record<string, string>;
  /**
   * Columns written as raw text, so Excel does not turn "$1,250", "00457"
   * or "2026-03-15" into numbers or dates on the way in.
   */
  textColumns?: string[];
}

/** A labelled single cell: an input the formulas read, or an answer cell. */
export interface LabelledCell {
  address: string;
  /** Written in the cell to the left of `address`, or above it. */
  label: string;
  labelPosition?: "left" | "above";
  value?: CellValue;
  format?: string;
  /** false keeps a long value from widening its column; it overflows instead. */
  autofit?: boolean;
  /** Formats for the cells a result spills into (spilled cells don't inherit). */
  areas?: { range: string; format: string }[];
}

/**
 * A two-way summary: row headers down, column headers across, and an empty
 * highlighted body the student fills with one formula dragged across.
 */
export interface GridSpec {
  /** Top-left corner cell (holds `corner`). */
  anchor: string;
  corner: string;
  rowHeaders: CellValue[];
  colHeaders: CellValue[];
  format?: string;
}

export interface SheetSpec {
  name: string;
  title: string;
  subtitle: string;
  tables: TableSpec[];
  grids?: GridSpec[];
  inputs?: LabelledCell[];
  answers?: LabelledCell[];
}

/**
 * What a task is graded on. `fns` lists functions that must ALL appear;
 * one entry may hold alternatives separated by "|" ("VLOOKUP|XLOOKUP").
 * `minCounts` asks for a function several times in the same formula, e.g.
 * two SUMIFS added together for an "Norte o Sur" total.
 */
interface CheckBase {
  sheet: string;
  fns: string[];
  minCounts?: Record<string, number>;
  /** Text must match exactly: case and inner/outer spaces count. */
  exact?: boolean;
}

export type TaskCheck =
  | (CheckBase & { kind: "column"; table: string; column: string; expected: CellValue[] })
  | (CheckBase & { kind: "cell"; address: string; expected: CellValue })
  | (CheckBase & { kind: "block"; address: string; expected: CellValue[][] })
  /** A dynamic-array result: the whole range spilled from `address`. */
  | (CheckBase & { kind: "spill"; address: string; expected: CellValue[][] });

export interface FuncionProjectSpec {
  projectId: string;
  sheets: SheetSpec[];
  /** taskId → check */
  checks: Record<number, TaskCheck>;
}

/* ── Address helpers shared by builder and grader ── */

export function columnLetter(index: number): string {
  let n = index + 1;
  let letters = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function columnIndex(letters: string): number {
  return letters.split("").reduce((acc, ch) => acc * 26 + ch.charCodeAt(0) - 64, 0) - 1;
}

export function parseAddress(address: string): { col: number; row: number } {
  const match = /^([A-Z]+)(\d+)$/.exec(address);
  if (!match) throw new Error(`Dirección inválida: ${address}`);
  return { col: columnIndex(match[1]), row: Number(match[2]) };
}

export function allHeaders(table: TableSpec): string[] {
  return [...table.headers, ...(table.answerColumns ?? [])];
}

/** "F5:F34"-style body range of one column of a table. */
export function tableColumnRange(table: TableSpec, column: string): string {
  const index = allHeaders(table).indexOf(column);
  if (index < 0) throw new Error(`La tabla ${table.name} no tiene la columna ${column}`);
  const { col, row } = parseAddress(table.anchor);
  const letter = columnLetter(col + index);
  return `${letter}${row + 1}:${letter}${row + table.rows.length}`;
}

/** "C6:F9"-style body of a grid. */
export function gridBodyRange(grid: GridSpec): string {
  const { col, row } = parseAddress(grid.anchor);
  const first = `${columnLetter(col + 1)}${row + 1}`;
  const last = `${columnLetter(col + grid.colHeaders.length)}${row + grid.rowHeaders.length}`;
  return `${first}:${last}`;
}

export function findTable(spec: FuncionProjectSpec, name: string): TableSpec {
  for (const sheet of spec.sheets) {
    const table = sheet.tables.find((item) => item.name === name);
    if (table) return table;
  }
  throw new Error(`No existe la tabla ${name}`);
}

/* ── Text helpers ── */

/**
 * Task description with one condition per bullet, so rules with several
 * cases read at a glance: intro, the list, and an optional closing line.
 */
export function conditions(intro: string, items: string[], outro?: string): string {
  const list = items.map((item) => `<li>${item}</li>`).join("");
  return `<p>${intro}</p><ul class="condiciones">${list}</ul>${outro ? `<p>${outro}</p>` : ""}`;
}

/* ── Data helpers ── */

/** Deterministic pseudo-random generator, so every workbook gets the same data. */
export function seededRandom(seed: number) {
  let state = seed >>> 0;
  const next = () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967296;
  };
  return {
    next,
    int: (min: number, max: number) => min + Math.floor(next() * (max - min + 1)),
    pick: <T,>(items: readonly T[]): T => items[Math.floor(next() * items.length)],
    round: (min: number, max: number, step: number) =>
      Math.round((min + next() * (max - min)) / step) * step,
  };
}

/** Excel serial number for a yyyy-mm-dd date (1900 date system). */
export function excelDate(isoDate: string): number {
  const [y, m, d] = isoDate.split("-").map(Number);
  return (Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86400000;
}

export function isoFromSerial(serial: number): string {
  const date = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
  return date.toISOString().slice(0, 10);
}

export const NOMBRES = [
  "Ana López", "Luis Martínez", "Marta Sánchez", "Jorge Ramírez", "Sofía Torres",
  "Carlos Herrera", "Lucía Morales", "Diego Castillo", "Valeria Ruiz", "Andrés Flores",
  "Paola Jiménez", "Ricardo Vargas", "Daniela Ortiz", "Fernando Cruz", "Gabriela Reyes",
  "Miguel Navarro", "Isabel Romero", "Javier Medina", "Camila Aguilar", "Raúl Domínguez",
  "Elena Vega", "Pablo Guerrero", "Natalia Peña", "Héctor Ríos", "Mariana Silva",
  "Tomás Delgado", "Regina Campos", "Emilio Rojas", "Fernanda Luna", "Óscar Cabrera",
  "Renata Molina", "Iván Salazar", "Ximena Fuentes", "Adrián Paredes", "Carolina Ibarra",
] as const;
